import { MonitoreoReferencia } from "../../../generated/grpc/monitoreo_sesion_pb";

/**
 * Motor de evaluación del monitoreo: convierte lo que midió el monitor en un
 * estado de semáforo (especificación v1.0, sección 5).
 *
 * **Por qué existe un solo lugar para esto.** Hasta ahora la regla estaba
 * repartida: nueve archivos leían `rangoBajo` y `rangoMedio` del catálogo y
 * decidían el color por su cuenta, en 55 lugares distintos. Con eso, un cambio
 * de criterio obliga a encontrar los 55 y confiar en no haberse salteado
 * ninguno —y el reporte puede decir "alto" donde el mapa dice "medio" sin que
 * nadie lo note—. Acá se decide una vez y los nueve consumen el resultado.
 *
 * **Evalúa contra las referencias congeladas de la sesión, no contra el
 * catálogo vigente.** Los umbrales los define el cliente y los cambia cuando
 * quiere; leyendo los de hoy, subir uno en octubre repintaría los informes de
 * agosto. Un informe firmado tiene que decir lo mismo dentro de un año.
 *
 * ⚠️ **Dos reglas están pendientes de confirmación de SEXTANTE** y se
 * implementan como las deja la especificación (supuestos S1 y S2, página 32).
 * El documento pide confirmarlas antes de cerrar la fase 2, no antes de
 * empezarla. Cada una está en su propia función y marcada, así que la respuesta
 * se aplica en un solo lugar:
 *
 * - **S1** — {@link peorEstado}: el estado del organismo es el peor entre nivel
 *   e incidencia.
 * - **S2** — {@link estadoDeUnValor}: un valor igual al corte ya pasa al nivel
 *   siguiente.
 */

/** Unidades en que se mide un organismo (`Constantes.Catalogos.UnidadOrganismo`). */
export const UNIDAD = {
  acarosLupada: "ACAROS_LUPADA",
  individuosArbol: "INDIVIDUOS_ARBOL",
  porcentajeArboles: "PORCENTAJE_ARBOLES",
  presenciaCritica: "PRESENCIA_CRITICA",
} as const;

/** Grupo de los depredadores naturales. Nunca alertan: su presencia es buena. */
export const GRUPO_BENEFICO = "BENEFICO";

/** Lupadas por árbol: el divisor de {@link UNIDAD.acarosLupada}. El monitor
 *  cuenta sobre cuatro lupadas y el umbral está expresado por lupada, así que
 *  sin dividir toda lectura de ácaros queda cuatro veces más alta. */
export const LUPADAS_POR_ARBOL = 4;

/** Estados del semáforo, de mejor a peor. */
export type MonitoreoEstado = "ok" | "warn" | "alert";

/** Lo que se midió de un organismo en un lote, con la referencia que le toca. */
export type ResultadoOrganismo = {
  /** Promedio de conteo por árbol (Σcantidad / muestra), tal como lo calcula el
   *  servidor. No es el nivel todavía: la unidad decide cómo se convierte. */
  promedio: number;
  /** Árboles de la muestra con el organismo presente (conteo > 0). */
  afectados: number;
  /** Árboles evaluados en el lote. Es el divisor de todo promedio e
   *  incidencia —nunca los árboles sugeridos— (regla 1 de 4.4). */
  muestra: number;
  /** Con qué valores se evaluó, congelado el día de la sesión. `null` cuando la
   *  sesión es anterior al congelado: no hay con qué comparar y el organismo se
   *  muestra sin color. */
  referencia: MonitoreoReferencia | null;
};

/** El resultado ya interpretado. */
export type Evaluacion = {
  /** Cómo se mide el organismo. Viaja **dentro** de la evaluación y no al lado:
   *  el número y su unidad no se pueden separar sin volverse ilegibles, y
   *  pasarlos como dos argumentos invita a que se desincronicen. */
  unidad: string;
  /** El valor en la unidad del organismo, o `null` si esa unidad no define un
   *  nivel (los que se miden solo por porcentaje de árboles). */
  nivel: number | null;
  /** Porcentaje de árboles afectados. */
  incidencia: number;
  /** Estado que da el nivel por sí solo; `null` si no hay nivel o no hay cortes. */
  estadoNivel: MonitoreoEstado | null;
  /** Estado que da la incidencia por sí sola; `null` si no hay cortes. */
  estadoIncidencia: MonitoreoEstado | null;
  /** El estado del organismo. **`null` significa "no se puede evaluar"**: sin
   *  referencia, o benéfico. En pantalla eso es gris, y gris no es verde. */
  estado: MonitoreoEstado | null;
  /** Qué tan cerca está del corte de alerta (1 = justo en el corte). Sirve para
   *  ordenar por severidad entre organismos con unidades distintas, que de otro
   *  modo no se pueden comparar: 0,3 ácaros por lupada y 30 individuos por
   *  árbol no se ordenan por el número crudo. */
  proporcion: number;
};

/** Peso de cada estado para ordenar por severidad (mayor = peor). */
export const ESTADO_RANK: Record<MonitoreoEstado, number> = {
  alert: 2,
  warn: 1,
  ok: 0,
};

/** Etiqueta visible de cada estado. */
export const ESTADO_LABEL: Record<MonitoreoEstado, string> = {
  alert: "Alerta",
  warn: "Precaución",
  ok: "Sin presión",
};

/**
 * Porcentaje de árboles afectados.
 *
 * Con `muestra` en cero da 0 y no `NaN`: un lote sin árboles evaluados no es un
 * lote con incidencia desconocida, es un lote que no se recorrió, y eso lo
 * distingue quien lo muestra —por la ausencia del lote, no por un número roto—.
 */
export function calcularIncidencia(afectados: number, muestra: number): number {
  if (muestra <= 0) return 0;
  return (afectados / muestra) * 100;
}

/**
 * El valor en la unidad del organismo.
 *
 * Es la conversión que separa "lo que contó el monitor" de "lo que dice el
 * umbral". Los ácaros se cuentan sobre cuatro lupadas por árbol y su umbral
 * está por lupada; los organismos que se miden solo por porcentaje de árboles
 * no tienen nivel; y en los críticos el nivel es la cantidad de árboles, porque
 * lo que importa es si apareció.
 */
export function calcularNivel(resultado: ResultadoOrganismo): number | null {
  const unidad = resultado.referencia?.unidad ?? UNIDAD.individuosArbol;
  switch (unidad) {
    case UNIDAD.acarosLupada:
      return resultado.promedio / LUPADAS_POR_ARBOL;
    case UNIDAD.porcentajeArboles:
      return null;
    case UNIDAD.presenciaCritica:
      return resultado.afectados;
    default:
      return resultado.promedio;
  }
}

/**
 * Estado de un valor contra sus dos cortes.
 *
 * ⚠️ **Supuesto S2, pendiente de confirmación**: los cortes son inclusivos
 * hacia arriba — un valor **igual** a `bajoMax` ya es `warn`, y uno igual a
 * `medioMax` ya es `alert`. Cae justo donde más casos hay, porque los conteos
 * reales dan muchos valores redondos que coinciden con el corte: con el
 * criterio contrario, el tercer ejemplo de la página 12 de la especificación
 * (nivel 0,10 con corte bajo 0,1) saldría verde en vez de amarillo.
 *
 * Un corte **nulo o cero se ignora**, porque cero no es un umbral: tratándolo
 * como tal, cualquier detección caería en alerta. Si no queda ningún corte
 * utilizable el estado es `null` —no evaluable—, que es distinto de `ok`.
 *
 * Nótese la consecuencia de ignorar el corte nulo: con `bajoMax` definido y
 * `medioMax` sin definir, el valor **nunca llega a `alert`**. Es deliberado;
 * `medioMax` es el umbral de daño económico, y sin ese número el sistema no
 * puede afirmar que hay daño económico. La librería v4 no trae ningún caso así
 * —sus cortes vienen en pareja o no vienen—, pero una finca sí puede crearlo
 * ajustando solo uno de los dos.
 */
export function estadoDeUnValor(
  valor: number | null,
  bajoMax: number | undefined,
  medioMax: number | undefined,
): MonitoreoEstado | null {
  if (valor === null) return null;

  const bajo = bajoMax && bajoMax > 0 ? bajoMax : null;
  const medio = medioMax && medioMax > 0 ? medioMax : null;
  if (bajo === null && medio === null) return null;

  if (medio !== null && valor >= medio) return "alert";
  if (bajo !== null && valor >= bajo) return "warn";
  return "ok";
}

/**
 * El peor de dos estados, ignorando los que no se pudieron evaluar.
 *
 * ⚠️ **Supuesto S1, pendiente de confirmación** (la especificación pide
 * confirmarlo antes de cerrar la fase 2). Pocos individuos repartidos en muchos
 * árboles da nivel bajo e incidencia alta, y el documento resuelve ese lote
 * como alto: manda la peor de las dos lecturas.
 *
 * Los `null` no cuentan: un organismo con cortes de incidencia pero no de nivel
 * se evalúa por incidencia, no queda sin evaluar.
 */
export function peorEstado(
  a: MonitoreoEstado | null,
  b: MonitoreoEstado | null,
): MonitoreoEstado | null {
  if (a === null) return b;
  if (b === null) return a;
  return ESTADO_RANK[a] >= ESTADO_RANK[b] ? a : b;
}

/**
 * Qué tan cerca del corte de alerta está la lectura.
 *
 * Es lo que permite ordenar por severidad organismos con unidades distintas:
 * 0,3 ácaros por lupada y 30 individuos por árbol no se comparan por el número
 * crudo. Sin ningún corte de alerta no hay con qué normalizar y se devuelve el
 * valor tal cual, que al menos ordena entre organismos de la misma unidad.
 */
export function calcularProporcion(
  nivel: number | null,
  incidencia: number,
  referencia: MonitoreoReferencia | null,
): number {
  const proporciones: number[] = [];

  const nivelMedio = referencia?.nivelMedioMax;
  if (nivel !== null && nivelMedio && nivelMedio > 0) {
    proporciones.push(nivel / nivelMedio);
  }

  const incidenciaMedio = referencia?.incidenciaMedioMax;
  if (incidenciaMedio && incidenciaMedio > 0) {
    proporciones.push(incidencia / incidenciaMedio);
  }

  if (proporciones.length === 0) return nivel ?? incidencia;
  return Math.max(...proporciones);
}

/**
 * Evalúa un organismo en un lote.
 *
 * El orden de las reglas importa y no es arbitrario:
 *
 * 1. **Benéfico** primero. Es el único caso donde la respuesta correcta es "no
 *    aplica" por más alto que sea el conteo: son depredadores naturales y su
 *    presencia es una buena noticia. Si se evaluara como plaga, un lote lleno
 *    de crisópidos se pintaría de rojo.
 * 2. **Crítico** después, porque ignora los umbrales: un solo árbol con HLB es
 *    alerta, y mirar los cortes antes daría `ok` donde corresponde `alert`.
 * 3. **Sin referencia**: no hay con qué comparar. Los números se muestran y el
 *    estado queda `null` —gris, "sin valores de referencia"—, que **no es** lo
 *    mismo que verde.
 * 4. Recién entonces los dos valores contra sus cortes, y el peor manda (S1).
 */
export function evaluar(resultado: ResultadoOrganismo): Evaluacion {
  const { referencia } = resultado;
  const incidencia = calcularIncidencia(resultado.afectados, resultado.muestra);
  const nivel = calcularNivel(resultado);
  const proporcion = calcularProporcion(nivel, incidencia, referencia);

  const unidad = referencia?.unidad ?? UNIDAD.individuosArbol;
  const base = { unidad, nivel, incidencia, proporcion };

  // 1. Benéfico: nunca alerta.
  if (referencia?.grupoId === GRUPO_BENEFICO) {
    return { ...base, estadoNivel: null, estadoIncidencia: null, estado: null };
  }

  // 2. Crítico: cualquier presencia es alerta, sin gradiente.
  const esCritico =
    referencia?.esCritico === true ||
    referencia?.unidad === UNIDAD.presenciaCritica;
  if (esCritico) {
    const estado: MonitoreoEstado = resultado.afectados > 0 ? "alert" : "ok";
    return { ...base, estadoNivel: estado, estadoIncidencia: null, estado };
  }

  // 3. Sin referencia: se muestra, no se evalúa.
  if (referencia === null) {
    return { ...base, estadoNivel: null, estadoIncidencia: null, estado: null };
  }

  // 4. Los dos valores contra sus cortes; el peor manda (S1).
  const estadoNivel = estadoDeUnValor(
    nivel,
    referencia.nivelBajoMax,
    referencia.nivelMedioMax,
  );
  const estadoIncidencia = estadoDeUnValor(
    incidencia,
    referencia.incidenciaBajoMax,
    referencia.incidenciaMedioMax,
  );

  return {
    ...base,
    estadoNivel,
    estadoIncidencia,
    estado: peorEstado(estadoNivel, estadoIncidencia),
  };
}
